import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Car, CheckCircle, LogIn, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import {
	getRegistrationData,
	registerCar,
	updateRegistration,
	withdrawRegistration,
} from "@/lib/server/registration";

export const Route = createFileRoute("/events/$eventId/register")({
	loader: ({ params }) => getRegistrationData({ data: params.eventId }),
	component: RegisterPage,
});

function RegisterPage() {
	const { event, userId, existingCar, nextNumber } = Route.useLoaderData();
	const navigate = useNavigate();
	const formId = useId();
	const [confirm, confirmDialog] = useConfirm();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [withdrawing, setWithdrawing] = useState(false);

	const registrationOpen =
		(event.status === "setup" || event.status === "qualifying") &&
		event.registrationMode === "open";
	const canEdit = event.status !== "finals" && event.status !== "complete";

	// Admin-only registration mode — show message (even if logged in)
	if (event.registrationMode === "admin_only" && !existingCar && userId) {
		return (
			<div className="min-h-screen bg-background p-4 sm:p-6">
				<div className="max-w-lg mx-auto">
					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
					>
						<ArrowLeft size={16} />
						Back to {event.name}
					</Link>

					<div className="bg-card rounded-xl p-8 text-center">
						<Car size={48} className="text-primary mx-auto mb-4" />
						<h1 className="text-2xl text-foreground mb-2">
							Staff Registration Only
						</h1>
						<p className="text-muted-foreground">
							Registration for {event.name} is handled by event staff. Please
							see an organizer to register your car.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Not logged in
	if (!userId) {
		return (
			<div className="min-h-screen bg-background p-4 sm:p-6">
				<div className="max-w-lg mx-auto">
					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
					>
						<ArrowLeft size={16} />
						Back to {event.name}
					</Link>

					<div className="bg-card rounded-xl p-8 text-center">
						<LogIn size={48} className="text-primary mx-auto mb-4" />
						<h1 className="text-2xl text-foreground mb-2">
							Sign in to Register
						</h1>
						<p className="text-muted-foreground mb-6">
							You need an account to register a car for {event.name}.
						</p>
						<Link
							to="/login"
							search={{ redirect: `/events/${event.id}/register` }}
							className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
						>
							<LogIn size={16} />
							Sign In / Create Account
						</Link>
					</div>
				</div>
			</div>
		);
	}

	// Registration closed
	if (!registrationOpen && !existingCar) {
		return (
			<div className="min-h-screen bg-background p-4 sm:p-6">
				<div className="max-w-lg mx-auto">
					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
					>
						<ArrowLeft size={16} />
						Back to {event.name}
					</Link>

					<div className="bg-card rounded-xl p-8 text-center">
						<Car size={48} className="text-muted-foreground mx-auto mb-4" />
						<h1 className="text-2xl text-foreground mb-2">
							Registration Closed
						</h1>
						<p className="text-muted-foreground">
							Registration for {event.name} is no longer available.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Already registered - show edit form or read-only
	if (existingCar) {
		return (
			<div className="min-h-screen bg-background p-4 sm:p-6">
				{confirmDialog}
				<div className="max-w-lg mx-auto">
					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
					>
						<ArrowLeft size={16} />
						Back to {event.name}
					</Link>

					<div className="bg-success/10 border border-success/30 rounded-xl p-4 mb-6 flex items-center gap-3">
						<CheckCircle size={20} className="text-success shrink-0" />
						<div>
							<p className="text-foreground font-bold">You're registered!</p>
							<p className="text-muted-foreground text-sm">
								Car #{existingCar.carNumber} &mdash;{" "}
								{existingCar.checkedIn ? "Checked in" : "Not yet checked in"}
							</p>
						</div>
					</div>

					{canEdit ? (
						<EditRegistrationForm
							car={existingCar}
							event={event}
							formId={formId}
							loading={loading}
							setLoading={setLoading}
							error={error}
							setError={setError}
							onSave={() =>
								navigate({
									to: "/events/$eventId",
									params: { eventId: String(event.id) },
								})
							}
						/>
					) : (
						<div className="bg-card rounded-xl p-6">
							<h2 className="text-lg text-foreground mb-4">
								Your Registration
							</h2>
							<div className="grid grid-cols-2 gap-4 text-sm">
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Racer Name
									</p>
									<p className="text-foreground">{existingCar.driverName}</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Car Name
									</p>
									<p className="text-foreground">
										{existingCar.carName || "—"}
									</p>
								</div>
								<div>
									<p className="text-muted-foreground font-bold uppercase text-xs">
										Car Number
									</p>
									<p className="text-foreground">#{existingCar.carNumber}</p>
								</div>
								{existingCar.division && (
									<div>
										<p className="text-muted-foreground font-bold uppercase text-xs">
											Division
										</p>
										<p className="text-foreground">{existingCar.division}</p>
									</div>
								)}
							</div>
						</div>
					)}

					{canEdit && (
						<div className="mt-6 bg-destructive/10 border border-destructive/30 rounded-xl p-4">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-foreground font-bold text-sm">
										Withdraw Registration
									</p>
									<p className="text-muted-foreground text-xs">
										Remove your car from this event
									</p>
								</div>
								<button
									type="button"
									disabled={withdrawing}
									onClick={async () => {
										const ok = await confirm({
											title: "Withdraw Registration",
											description:
												"Are you sure you want to withdraw your car from this event?",
											confirmLabel: "Withdraw",
											variant: "destructive",
										});
										if (!ok) return;
										setWithdrawing(true);
										try {
											await withdrawRegistration({ data: existingCar.id });
											navigate({
												to: "/events/$eventId",
												params: { eventId: String(event.id) },
											});
										} catch (err) {
											setError(
												err instanceof Error
													? err.message
													: "Failed to withdraw",
											);
											setWithdrawing(false);
										}
									}}
									className="inline-flex items-center gap-1.5 px-4 py-2 bg-destructive hover:bg-destructive/80 text-foreground font-bold rounded-lg transition-colors text-sm"
								>
									<Trash2 size={14} />
									{withdrawing ? "Withdrawing..." : "Withdraw"}
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	// New registration form
	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-lg mx-auto">
				<Link
					to="/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<h1 className="text-3xl text-foreground mb-2">Register Your Car</h1>
				<p className="text-muted-foreground mb-6">
					{event.name} &mdash; {event.date}
					{event.location && ` · ${event.location}`}
				</p>

				{error && (
					<div className="bg-destructive/30 border border-destructive text-foreground text-sm rounded-lg p-3 mb-4">
						{error}
					</div>
				)}

				<form
					onSubmit={async (e) => {
						e.preventDefault();
						setLoading(true);
						setError("");
						const form = new FormData(e.currentTarget);
						try {
							await registerCar({
								data: {
									eventId: event.id,
									driverName: form.get("driverName") as string,
									carName: (form.get("carName") as string) || "",
									carNumber: form.get("carNumber")
										? Number(form.get("carNumber"))
										: null,
									division: (form.get("division") as string) || "",
								},
							});
							navigate({
								to: "/events/$eventId/register",
								params: { eventId: String(event.id) },
								reloadDocument: true,
							});
						} catch (err) {
							setError(
								err instanceof Error ? err.message : "Registration failed",
							);
						} finally {
							setLoading(false);
						}
					}}
					className="bg-card rounded-xl p-6 space-y-4"
				>
					<div>
						<label htmlFor={`${formId}-driver`} className="block text-sm mb-1">
							Racer Name *
						</label>
						<input
							id={`${formId}-driver`}
							name="driverName"
							type="text"
							required
							className="w-full px-4 py-2"
							placeholder="Child's name"
						/>
					</div>

					<div>
						<label htmlFor={`${formId}-car`} className="block text-sm mb-1">
							Car Name
						</label>
						<input
							id={`${formId}-car`}
							name="carName"
							type="text"
							className="w-full px-4 py-2"
							placeholder="e.g., Lightning Bolt"
						/>
					</div>

					{event.carNumberMode === "racer_choice" && (
						<div>
							<label
								htmlFor={`${formId}-number`}
								className="block text-sm mb-1"
							>
								Car Number (optional)
							</label>
							<input
								id={`${formId}-number`}
								name="carNumber"
								type="number"
								min="1"
								className="w-full px-4 py-2"
								placeholder={`Leave blank for auto-assign (next: #${nextNumber})`}
							/>
							<p className="text-muted-foreground text-xs mt-1">
								If blank, you'll be assigned #{nextNumber}
							</p>
						</div>
					)}

					{event.carNumberMode === "auto" && (
						<div>
							<p className="text-sm text-muted-foreground">
								Car Number:{" "}
								<strong className="text-foreground">Auto-assigned</strong>
							</p>
						</div>
					)}

					{event.divisions.length > 0 && (
						<div>
							<label
								htmlFor={`${formId}-division`}
								className="block text-sm mb-1"
							>
								Division
							</label>
							<select
								id={`${formId}-division`}
								name="division"
								className="w-full px-4 py-2"
							>
								<option value="">Select a division</option>
								{event.divisions.map((div) => (
									<option key={div} value={div}>
										{div}
									</option>
								))}
							</select>
						</div>
					)}

					<button
						type="submit"
						disabled={loading}
						className="w-full py-3 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-lg uppercase"
					>
						{loading ? "Registering..." : "Register Car"}
					</button>
				</form>
			</div>
		</div>
	);
}

function EditRegistrationForm({
	car,
	event,
	formId,
	loading,
	setLoading,
	error,
	setError,
	onSave,
}: {
	car: {
		id: number;
		driverName: string;
		carName: string | null;
		carNumber: number;
		division: string | null;
	};
	event: { id: number; divisions: string[] };
	formId: string;
	loading: boolean;
	setLoading: (v: boolean) => void;
	error: string;
	setError: (v: string) => void;
	onSave: () => void;
}) {
	return (
		<form
			onSubmit={async (e) => {
				e.preventDefault();
				setLoading(true);
				setError("");
				const form = new FormData(e.currentTarget);
				try {
					await updateRegistration({
						data: {
							carId: car.id,
							driverName: form.get("driverName") as string,
							carName: (form.get("carName") as string) || "",
							division: (form.get("division") as string) || "",
						},
					});
					onSave();
				} catch (err) {
					setError(err instanceof Error ? err.message : "Update failed");
				} finally {
					setLoading(false);
				}
			}}
			className="bg-card rounded-xl p-6 space-y-4"
		>
			<h2 className="text-lg text-foreground">Edit Registration</h2>

			{error && (
				<div className="bg-destructive/30 border border-destructive text-foreground text-sm rounded-lg p-3">
					{error}
				</div>
			)}

			<div>
				<label htmlFor={`${formId}-driver`} className="block text-sm mb-1">
					Racer Name *
				</label>
				<input
					id={`${formId}-driver`}
					name="driverName"
					type="text"
					required
					defaultValue={car.driverName}
					className="w-full px-4 py-2"
				/>
			</div>

			<div>
				<label htmlFor={`${formId}-car`} className="block text-sm mb-1">
					Car Name
				</label>
				<input
					id={`${formId}-car`}
					name="carName"
					type="text"
					defaultValue={car.carName ?? ""}
					className="w-full px-4 py-2"
				/>
			</div>

			<div>
				<p className="text-sm text-muted-foreground">
					Car Number:{" "}
					<strong className="text-foreground">#{car.carNumber}</strong>
					<span className="text-xs ml-1">(assigned by admin)</span>
				</p>
			</div>

			{event.divisions.length > 0 && (
				<div>
					<label htmlFor={`${formId}-division`} className="block text-sm mb-1">
						Division
					</label>
					<select
						id={`${formId}-division`}
						name="division"
						defaultValue={car.division ?? ""}
						className="w-full px-4 py-2"
					>
						<option value="">None</option>
						{event.divisions.map((div) => (
							<option key={div} value={div}>
								{div}
							</option>
						))}
					</select>
				</div>
			)}

			<button
				type="submit"
				disabled={loading}
				className="w-full py-3 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
			>
				{loading ? "Saving..." : "Save Changes"}
			</button>
		</form>
	);
}
